import type { DocumentViewerMessage } from '@st/document-viewer-kit'
import { toAnnotationsPluginMessage } from '@st/document-viewer-kit/plugins/annotations'
import type { Point, Size } from '@st/geom'
import { clsx } from 'clsx'
import type { StampTemplate } from '../../annotations'
import { useAnnotationsContext } from '../../annotations-context'
import { recomputeStampLayoutAndStyle } from '../stamps-core/stamp-layout'
import { formatSignOffAuthor } from './model'

const STAMP_START_ORIGIN: Point = { x: 10, y: 10 }
const STAMP_SPACING: number = 0
const STAMP_SIZE: Size = { width: 80, height: 16 }

type StatusStampTemplate = {
  stampCode: string
  backgroundColor: string
  color: string
  label: string
}

const STATUS_STAMP_TEMPLATES: StatusStampTemplate[] = [
  { stampCode: 'prepared', backgroundColor: '#ef4444', color: '#ffffff', label: 'Prepared' },
  { stampCode: 'reviewed', backgroundColor: '#536cf1', color: '#ffffff', label: 'Reviewed' },
  { stampCode: 'reviewed2', backgroundColor: '#9c63ff', color: '#ffffff', label: 'Reviewed 2' }
]

const STAMP_TEMPLATES: StampTemplate[] = STATUS_STAMP_TEMPLATES.map((t, index) => {
  return {
    id: t.stampCode,
    label: t.label,
    category: 'sign_off',
    annotation: recomputeStampLayoutAndStyle(
      {
        id: t.stampCode,
        time: '2000-01-01T00:00:00.000Z',
        pageNumber: 1,
        text: t.label,
        type: 'stamp',
        stampType: 'sign_off',
        stampSize: STAMP_SIZE.height,
        stampCode: t.stampCode,
        bounds: {
          x: STAMP_START_ORIGIN.x + (STAMP_SIZE.width + STAMP_SPACING) * index,
          y: STAMP_START_ORIGIN.y,
          width: STAMP_SIZE.width,
          height: STAMP_SIZE.height
        },
        backgroundColor: t.backgroundColor,
        color: t.color,
        font: {
          family: 'Helvetica',
          weight: 'normal',
          size: 10
        }
      },
      { width: STAMP_SIZE.width }
    )
  } satisfies StampTemplate
})

export function StatusStampsDocumentBanner({
  title,
  sendViewerMessage
}: {
  title: string
  sendViewerMessage: (message: DocumentViewerMessage) => void
}) {
  const { currentUser, stampAnnotations } = useAnnotationsContext()

  return (
    <div className="pointer-events-auto flex items-center justify-between gap-x-6 bg-gray-200 px-5 text-base text-gray-900">
      <div>{title}</div>
      <div className="flex">
        {STAMP_TEMPLATES.map((template, index) => {
          const matchingStamps = stampAnnotations.filter(
            (s) => s.stampCode == template.annotation.stampCode
          )
          const isActive = matchingStamps.length > 0

          return (
            <button
              key={index}
              style={{
                backgroundColor: isActive ? template.annotation.backgroundColor : undefined,
                color: isActive ? template.annotation.color : undefined
              }}
              className={clsx('w-24 px-2 py-1', {
                'rounded-l': index == 0,
                'rounded-r': index == STATUS_STAMP_TEMPLATES.length - 1,
                'bg-white text-gray-900': !isActive
              })}
              onClick={() => {
                if (isActive) {
                  sendViewerMessage(
                    toAnnotationsPluginMessage({
                      type: 'deleteAnnotation',
                      annotationId: matchingStamps[0].id
                    })
                  )
                } else {
                  const {
                    id: _templateId,
                    time: _templateTime,
                    ...annotationTemplate
                  } = template.annotation
                  const annotation = {
                    ...annotationTemplate,
                    authorId: currentUser?.id,
                    pageNumber: 1,
                    stampSize: STAMP_SIZE.height,
                    text: formatSignOffAuthor(template.annotation.text, currentUser?.name),
                    bounds: {
                      x: STAMP_START_ORIGIN.x + (STAMP_SIZE.width + STAMP_SPACING) * index,
                      y: STAMP_START_ORIGIN.y,
                      width: STAMP_SIZE.width,
                      height: STAMP_SIZE.height
                    }
                  }
                  sendViewerMessage(
                    toAnnotationsPluginMessage({
                      type: 'requestAnnotationCreation',
                      annotationDraft: annotation
                    })
                  )
                }
              }}
            >
              {template.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
